import React from "react";
import { CodeBlock } from "@ui/CodeBlock";
import InternalLink from "../../components/InternalLink";

function DeveloperIntro() {
    return (
        <div className="max-w-3xl mx-auto py-10 px-4 sm:px-6 lg:py-12 lg:px-8">
            <h1 className="text-3xl font-bold text-gray-900  mb-8">
                Introduction to Agent UI
            </h1>

            <p className="text-gray-600  mb-8">
                Agent UI provides flexible integration options to suit your
                development needs. Currently, we offer two primary methods of
                integration:
            </p>

            <section className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900  mb-4">
                    Integration Methods
                </h2>

                <div className="space-y-8">
                    <div>
                        <h3 className="text-xl font-semibold text-gray-900  mb-3">
                            1. Node.js SDK
                        </h3>
                        <p className="text-gray-600  mb-4">
                            Our official Node.js SDK provides a clean, type-safe
                            way to integrate with Agent UI. A simple Node.js for
                            registering functions.
                        </p>
                        <CodeBlock language="javascript">
                            {`import { SDKClient } from 'agent-ui-sdk';

const client = new SDKClient({
  apiKey: 'your-api-key'
});

// Register a function
await client.registerFunction({
  path: '/api/my-function',
  method: 'POST',
  parameters: {
    userId: {
      type: 'string',
      description: 'User identifier'
    }
  },
  headers: {
    "Authorization": "Bearer your-api-key"
  }
});`}
                        </CodeBlock>
                        <div className="mt-4">
                            <InternalLink
                                to="/docs/developer/node-sdk"
                                className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                            >
                                Learn more about the SDK →
                            </InternalLink>
                        </div>
                    </div>

                    <div>
                        <h3 className="text-xl font-semibold text-gray-900  mb-3">
                            2. Direct API Integration
                        </h3>
                        <p className="text-gray-600  mb-4">
                            For simpler integrations or different programming
                            languages, you can directly use our REST API
                            endpoints with standard HTTP requests.
                        </p>
                        <CodeBlock language="bash">
                            {`curl -X POST https://api.agentui.ai/function-groups/functions \\
  -H "Content-Type: application/json" \\
  -d '{
    "privateKey": "your-api-key",
    "path": "/api/my-function",
    "type": "OUTGOING",
    "method": "POST",
    "parameters": {
      "userId": {
        "type": "string",
        "description": "User identifier"
      }
    },
    "headers": {
        "Authorization": "Bearer your-api-key"
    },
    "contentType": "JSON",
    "requiresAuth": false,
    "timeout": 30000,
    "retryPolicy": null,
    "agentDataField": null,
    "aiInstructions": null
  }'`}
                        </CodeBlock>
                    </div>
                </div>
            </section>

            <section className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900  mb-4">
                    Choosing an Integration Method
                </h2>
                <div className="prose dark:prose-invert">
                    <ul className="list-disc pl-6 text-gray-600  space-y-2">
                        <li>
                            <strong>Choose the SDK</strong> if you're working
                            with Node.js and want the most streamlined
                            development experience with type safety and built-in
                            utilities.
                        </li>
                        <li>
                            <strong>Choose Direct API Integration</strong> if
                            you're working with a different programming
                            language, need more control over the integration, or
                            prefer a simpler approach.
                        </li>
                    </ul>
                </div>
            </section>

            <section className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900  mb-4">
                    Next Steps
                </h2>
                <p className="text-gray-600  mb-4">
                    Ready to get started? Choose your preferred integration
                    method:
                </p>
                <div className="space-y-2">
                    <div>
                        <InternalLink
                            to="/docs/developer/node-sdk"
                            className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                        >
                            • SDK Documentation
                        </InternalLink>
                    </div>
                    <div>
                        <InternalLink
                            to="/docs/developer/api-reference"
                            className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                        >
                            • API Reference
                        </InternalLink>
                    </div>
                </div>
            </section>
        </div>
    );
}

export default DeveloperIntro;
